import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { Archive, Fingerprint, AlertTriangle, EyeOff, Languages, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { k: "archaeology", label: "Rights Archaeology", Icon: Archive },
    { k: "similarity", label: "Similarity Scan", Icon: Fingerprint },
    { k: "redflags", label: "Red Flag Scanner", Icon: AlertTriangle },
    { k: "redact", label: "Auto-Redact", Icon: EyeOff },
    { k: "dubbing", label: "Smart Dubbing", Icon: Languages },
];

export default function AIStudioHub() {
    const [tab, setTab] = useState("archaeology");
    const [films, setFilms] = useState([]);
    const [filmId, setFilmId] = useState("");
    const [archForm, setArchForm] = useState({ original_year: 1985, known_credits: "" });
    const [archOut, setArchOut] = useState(null);
    const [simOut, setSimOut] = useState(null);
    const [rfText, setRfText] = useState("");
    const [rfOut, setRfOut] = useState(null);
    const [redText, setRedText] = useState("");
    const [redOut, setRedOut] = useState(null);
    const [langs, setLangs] = useState([]);
    const [dubSel, setDubSel] = useState([]);
    const [dubOrder, setDubOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const f = await api.get("/films"); setFilms(f.data);
            if (f.data[0]) setFilmId(f.data[0].id);
            const l = await api.get("/dubbing/languages"); setLangs(l.data);
        })();
    }, []);

    const run = async (fn) => {
        setLoading(true);
        try { await fn(); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI call failed"); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> AI Studio
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-3">AI that does <span className="silver-text italic">real</span> work.</h1>
                <p className="text-silver-400 mb-10 max-w-2xl">Rights archaeology for orphan works, similarity detection, legal red-flag scanning, GDPR-grade redaction, and turnkey AI dubbing.</p>

                <div className="mb-6 flex items-center gap-3 flex-wrap">
                    <span className="text-[11px] tracking-widest uppercase text-silver-500">Active film:</span>
                    <select value={filmId} onChange={(e) => setFilmId(e.target.value)} className="bg-ink-200 border border-white/10 px-3 py-2 text-sm rounded-sm" data-testid="ai-film-picker">
                        {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                    </select>
                </div>

                <div className="flex items-center gap-6 border-b border-white/5 mb-8 overflow-x-auto">
                    {TABS.map(({ k, label, Icon }) => (
                        <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap text-xs uppercase tracking-widest ${tab === k ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"}`} data-testid={`ai-tab-${k}`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {tab === "archaeology" && (
                    <div data-testid="archaeology-panel" className="max-w-3xl">
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <input type="number" placeholder="Original year" value={archForm.original_year} onChange={(e) => setArchForm({ ...archForm, original_year: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" />
                            <input placeholder="Known credits (comma-sep)" value={archForm.known_credits} onChange={(e) => setArchForm({ ...archForm, known_credits: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" />
                        </div>
                        <button disabled={loading} onClick={() => run(async () => {
                            const { data } = await api.post("/ai/rights-archaeology", {
                                film_id: filmId, original_year: Number(archForm.original_year),
                                known_credits: archForm.known_credits.split(",").map(s => s.trim()).filter(Boolean),
                            });
                            setArchOut(data);
                        })} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="archaeology-run-btn">
                            {loading ? "Reconstructing…" : "Reconstruct chain"}
                        </button>
                        {archOut && (
                            <div>
                                <div className="flex gap-6 mb-4">
                                    <div>
                                        <div className="text-[10px] tracking-widest uppercase text-silver-500">Orphan risk</div>
                                        <div className={`font-display text-3xl ${archOut.orphan_risk === "low" ? "text-emerald-300" : archOut.orphan_risk === "medium" ? "text-amber-300" : "text-red-300"}`}>{archOut.orphan_risk}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] tracking-widest uppercase text-silver-500">Estimated clean-up</div>
                                        <div className="font-display text-3xl silver-text">{archOut.estimated_months_to_clean}mo</div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    {archOut.reconstructed_chain.map((c, i) => (
                                        <div key={i} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-1">{c.role}</div>
                                            <div className="text-silver-200 text-sm mb-1">{c.likely_person_or_estate} <span className="text-silver-500">· {c.likely_residence}</span></div>
                                            <details className="text-silver-400 text-xs italic mt-2">
                                                <summary className="text-platinum not-italic text-[10px] tracking-widest uppercase cursor-pointer">Outreach draft</summary>
                                                <div className="mt-2 whitespace-pre-wrap">{c.outreach_template}</div>
                                            </details>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "similarity" && (
                    <div data-testid="similarity-panel" className="max-w-3xl">
                        <button disabled={loading} onClick={() => run(async () => {
                            const { data } = await api.post("/ai/similarity-scan", { film_id: filmId });
                            setSimOut(data);
                        })} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="similarity-run-btn">
                            {loading ? "Scanning…" : "Scan catalogue"}
                        </button>
                        {simOut && (
                            <div className="space-y-2">
                                <div className="text-silver-400 text-sm mb-3">{simOut.matches.length} matches for <span className="text-platinum">{simOut.query_title}</span></div>
                                {simOut.matches.map((m) => (
                                    <div key={m.film_id} className="flex items-center justify-between p-3 border border-white/10 rounded-sm bg-ink-200/40">
                                        <div>
                                            <div className="text-silver-200 text-sm">{m.title}</div>
                                            <div className="text-[11px] text-silver-500">{m.country} · {(m.shared_genres || []).join(", ")}</div>
                                        </div>
                                        <div className="text-platinum font-mono">{m.overlap_score}%</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "redflags" && (
                    <div data-testid="redflags-panel" className="max-w-3xl">
                        <textarea rows={6} placeholder="Paste synopsis or script excerpt…" value={rfText} onChange={(e) => setRfText(e.target.value)} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" />
                        <button disabled={loading || !rfText} onClick={() => run(async () => {
                            const { data } = await api.post("/ai/red-flags", { text: rfText });
                            setRfOut(data);
                        })} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="redflags-run-btn">
                            {loading ? "Scanning…" : "Scan for red flags"}
                        </button>
                        {rfOut && (
                            <div>
                                <div className={`inline-block px-3 py-1 rounded-sm text-[10px] tracking-widest uppercase mb-4 ${rfOut.overall_risk === "green" ? "bg-emerald-400/10 text-emerald-300 border border-emerald-400/30" : rfOut.overall_risk === "amber" ? "bg-amber-300/10 text-amber-200 border border-amber-300/30" : "bg-red-400/10 text-red-300 border border-red-400/30"}`}>
                                    Overall: {rfOut.overall_risk}
                                </div>
                                <div className="text-silver-300 text-sm mb-5">{rfOut.summary}</div>
                                <div className="space-y-2">
                                    {(rfOut.flags || []).map((f, i) => (
                                        <div key={i} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="text-silver-200 text-sm">{f.category}</div>
                                                <div className={`text-[10px] tracking-widest uppercase ${f.severity === "high" ? "text-red-300" : f.severity === "medium" ? "text-amber-300" : "text-silver-400"}`}>{f.severity}</div>
                                            </div>
                                            <div className="text-silver-400 text-xs italic mb-2">"{f.snippet}"</div>
                                            <div className="text-silver-300 text-sm mb-1"><span className="text-silver-500 text-[11px] tracking-widest uppercase">Issue: </span>{f.issue}</div>
                                            <div className="text-silver-300 text-sm"><span className="text-silver-500 text-[11px] tracking-widest uppercase">Mitigation: </span>{f.mitigation}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "redact" && (
                    <div data-testid="redact-panel" className="max-w-3xl">
                        <textarea rows={6} placeholder="Paste contract excerpt…" value={redText} onChange={(e) => setRedText(e.target.value)} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" />
                        <button disabled={loading || !redText} onClick={() => run(async () => {
                            const { data } = await api.post("/ai/redact", { text: redText });
                            setRedOut(data);
                        })} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="redact-run-btn">
                            {loading ? "Redacting…" : "Auto-redact"}
                        </button>
                        {redOut && (
                            <div>
                                <div className="flex gap-2 mb-3 flex-wrap">
                                    {(redOut.compliance || []).map((c, i) => (
                                        <span key={i} className="text-[10px] tracking-widest uppercase border border-emerald-400/30 text-emerald-300 bg-emerald-400/5 px-2 py-1 rounded-sm">{c}</span>
                                    ))}
                                </div>
                                <div className="bg-ink-200/50 border border-white/10 rounded-sm p-4 text-sm whitespace-pre-wrap text-silver-200 font-mono" data-testid="redact-output">
                                    {redOut.redacted_text}
                                </div>
                                <div className="text-[11px] text-silver-500 mt-2">Redacted: {Object.entries(redOut.count || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "dubbing" && (
                    <div data-testid="dubbing-panel" className="max-w-3xl">
                        <p className="text-silver-400 text-sm mb-5">Buyers can order AI-voiced dubbing for any completed acquisition. Delivery 48h, ElevenLabs integration (MOCKED).</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
                            {langs.map((l) => (
                                <button key={l.code} onClick={() => setDubSel(s => s.includes(l.code) ? s.filter(c => c !== l.code) : [...s, l.code])}
                                    className={`px-3 py-2 border rounded-sm text-xs ${dubSel.includes(l.code) ? "border-platinum text-platinum bg-platinum/5" : "border-white/10 text-silver-300 hover:border-white/30"}`}
                                    data-testid={`dub-lang-${l.code}`}>
                                    {l.name}<br /><span className="text-[10px] text-silver-500">${l.price_usd.toLocaleString()}</span>
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="text-[10px] tracking-widest uppercase text-silver-500">Total</div>
                            <div className="font-display text-2xl silver-text tabular-nums">${langs.filter(l => dubSel.includes(l.code)).reduce((a, b) => a + b.price_usd, 0).toLocaleString()}</div>
                        </div>
                        <button disabled={loading || dubSel.length === 0} onClick={() => run(async () => {
                            const { data } = await api.post("/dubbing/order", { film_id: filmId, language_codes: dubSel });
                            setDubOrder(data);
                            toast.success("Dubbing order queued (MOCKED)");
                        })} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm disabled:opacity-50" data-testid="dub-order-btn">
                            Order dubbing
                        </button>
                        {dubOrder && (
                            <div className="mt-5 border border-platinum/30 bg-platinum/5 rounded-sm p-4 text-sm text-silver-200">
                                Order #{dubOrder.id.slice(0, 8)} queued — ETA {dubOrder.eta_hours}h · ${dubOrder.total_usd.toLocaleString()}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
